import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { AutoCompleteDropdownComponent } from '../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { ErrorService } from '../../../shared/services/error-service';
import { WorkbookUploadService, UploadProgressEvent } from '../../../shared/services/workbook-upload.service';

import { SalesService, AgeingService, PeriodService, ClientService, AllPeriodItem } from '../../../../service/proxy';
import { ToastService } from '../../../shared/toast/toast.service';

@Component({
    selector: 'app-create-sales-batch',
    templateUrl: './create-sales-batch.component.html',
    styleUrls: ['./create-sales-batch.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteDropdownComponent
    ],

    providers: [
        SalesService,
        AgeingService,
        ClientService,
        PeriodService,
    ]
})
export class CreateSalesBatchComponent implements OnInit {
    public batchId: number = 0;
    public isSaving: boolean = false;
    public uploadProgress: number = 0;
    public uploadMessage: string = '';

    public selectedPeriodId: number | undefined = undefined;
    public periods: AllPeriodItem[] = [];

    public selectedClientId: number | undefined = undefined;
    public clients: { id: number; name: string }[] = [];

    public selectedFile: File | null = null;
    public selectedFileName: string = '';

    public formSubmitted: boolean = false;

    constructor(
        private router: Router,
        private salesService: SalesService,
        private ageingService: AgeingService,
        private periodService: PeriodService,
        private clientService: ClientService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private cdr: ChangeDetectorRef,
        private workbookUploadService: WorkbookUploadService
    ) {}

    ngOnInit(): void {
        this.getPeriods();
        this.getClients();
    }

    getPeriods(): void {
        this.periodService.getAllPeriods()
            .subscribe((data) => {
                this.periods = data.results || [];
                this.selectedPeriodId = this.periods[1]?.id ?? 0;
            });
    }

    getClients(): void {
        this.clientService.getAllClients()
            .subscribe({
                next: (response) => {
                    this.clients = response.results.map(client => ({
                        id: client.id,
                        name: client.clientName
                    }));
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    onPeriodSelectionChange(selectedId: number | null): void {
        this.selectedPeriodId = selectedId ?? undefined;
    }

    onClientSelectionChange(selectedId: number | null): void {
        this.selectedClientId = selectedId ?? undefined;
    }

    displayPeriod(option: any): string {
        const paddedMonth = ('0' + option.month).slice(-2);
        return `${option.year}-${paddedMonth}`;
    }

    onFileSelected(event: any): void {
        const input = event.target as HTMLInputElement;

        if (input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
            this.selectedFileName = input.files[0].name;
        }
        else {
            this.selectedFile = null;
            this.selectedFileName = '';
        }
    }

    onSave(form: any): void {
        this.formSubmitted = true;

        if (form.invalid || !this.selectedFile || this.selectedPeriodId === undefined || this.selectedClientId === undefined) {
            return;
        }

        this.isSaving = true;
        this.uploadProgress = 0;
        this.uploadMessage = 'Preparing upload...';

        // Server runs the legacy guards (pending sales batch, completed ageing
        // required) and returns 409 with a structured error if either fails.
        this.workbookUploadService.upload({
            importer: 'sales',
            file: this.selectedFile!,
            fields: {
                periodId: this.selectedPeriodId,
                clientId: this.selectedClientId,
            }
        }).subscribe({
            next: (event: UploadProgressEvent) => {
                this.uploadProgress = event.progress;
                this.uploadMessage = event.message;
                this.cdr.detectChanges();

                if (event.progress === 100) {
                    if (!event.batchId) {
                        this.toastService.show('Batch Id not available to open Import Wizard.', 'info');
                        this.isSaving = false;
                        return;
                    }

                    sessionStorage.setItem('isRedirected', true.toString());
                    this.router.navigate([`/sales-wizard/${event.batchId}`], {
                        queryParams: { clientId: this.selectedClientId }
                    });
                }
            },
            error: (error: any) => {
                if (error?.code === 'SALES_PENDING_BATCH_EXISTS' || error?.code === 'SALES_AGEING_NOT_COMPLETED') {
                    this.toastService.show(error.message, 'info');
                }
                else if (error?.message?.includes('xlsx')) {
                    this.toastService.show('Invalid file extension. Only .xlsx files are allowed.', 'warning');
                }
                else {
                    this.toastService.show(error?.message || 'Upload failed. Please try again.', 'error');
                }

                this.isSaving = false;
                this.uploadProgress = 0;
                this.uploadMessage = '';
            }
        });
    }

    onCancel(): void {
        this.router.navigate([`/sales`]);
    }
}